/**
 * Performance Analysis Tool
 * 
 * This tool helps developers analyze and debug performance issues,
 * particularly focused on slow API endpoints like /api/companies/fetchcompanies
 * 
 * Usage (from browser console):
 * - CareerChamp.analyzePerformance() - Show performance overview
 * - CareerChamp.analyzeApiPerformance() - Detailed API performance analysis
 * - CareerChamp.clearCompaniesCache() - Clear companies cache to test loading time
 * - CareerChamp.preloadCompanies() - Manually trigger company data preloading
 */

import { performanceMonitor } from './performanceMonitor';
import apiPerformanceMonitor from './apiPerformanceMonitor';
import companyDataManager from './companyDataManager';
import { apiCache } from './cacheManager';

// Create a global namespace for debugging tools
window.CareerChamp = window.CareerChamp || {};

// Add analysis tools to the namespace
window.CareerChamp.analyzePerformance = () => {
  console.group('🔍 Career Champ Performance Analysis');
  console.log('Running performance analysis...');
  
  // Get performance metrics
  const metrics = performanceMonitor.getMetrics();
  
  // Overview
  console.log('📊 Performance Overview:');
  
  // API metrics
  const apiMetrics = metrics.api || [];
  const apiCount = apiMetrics.length;
  const totalApiTime = apiMetrics.reduce((sum, m) => sum + m.duration, 0);
  const avgApiTime = apiCount > 0 ? totalApiTime / apiCount : 0;
  const slowestApi = apiMetrics.sort((a, b) => b.duration - a.duration)[0];
  
  console.log(`Total API calls: ${apiCount}`);
  console.log(`Total API time: ${(totalApiTime/1000).toFixed(2)}s`);
  console.log(`Average API call time: ${(avgApiTime/1000).toFixed(2)}s`);
  
  if (slowestApi) {
    console.log(`Slowest API call: ${slowestApi.name} (${(slowestApi.duration/1000).toFixed(2)}s)`);
  }
  
  // Cache stats
  const cacheStats = apiCache.getStats();
  console.log('📦 Cache Statistics:');
  console.log(`Cache size: ${cacheStats.size} items`);
  console.log(`Cache hits: ${cacheStats.hits}`);
  console.log(`Cache misses: ${cacheStats.misses}`);
  console.log(`Hit ratio: ${cacheStats.hitRatio.toFixed(2)}%`);
  
  console.groupEnd();
  
  return {
    apiStats: {
      count: apiCount,
      totalTime: totalApiTime,
      averageTime: avgApiTime,
      slowest: slowestApi
    },
    cacheStats
  };
};

// API specific analysis
window.CareerChamp.analyzeApiPerformance = () => {
  apiPerformanceMonitor.logApiPerformanceReport();
};

// Companies data management
window.CareerChamp.clearCompaniesCache = () => {
  companyDataManager.clearCompaniesCache();
};

window.CareerChamp.preloadCompanies = () => {
  console.log('🔄 Manually triggering company data preloading...');
  return companyDataManager.fetchCompanies()
    .then(data => {
      console.log(`✅ Preloaded ${data?.length || 0} companies`);
      return data;
    })
    .catch(err => {
      console.error('❌ Failed to preload companies:', err);
      throw err;
    });
};

// Enhanced API utilities
window.CareerChamp.flushApiCache = () => {
  apiCache.clearAll();
  console.log('🧹 All API cache cleared');
};

// Log available tools on startup
console.log('%cCareer Champ Performance Tools Available:', 'font-weight: bold; color: #4CAF50; font-size: 14px');
console.log('%c- CareerChamp.analyzePerformance()', 'color: #2196F3');
console.log('%c- CareerChamp.analyzeApiPerformance()', 'color: #2196F3');
console.log('%c- CareerChamp.clearCompaniesCache()', 'color: #2196F3');
console.log('%c- CareerChamp.preloadCompanies()', 'color: #2196F3');
console.log('%c- CareerChamp.flushApiCache()', 'color: #2196F3');

// Export a function to initialize these tools
export const initializePerformanceTools = () => {
  try {
    // Nothing extra needed here, initialization happens when this module is imported
    console.log('Performance analysis tools initialized');
    return () => {
      // Cleanup if needed
    };
  } catch (error) {
    console.error('Error initializing performance tools:', error);
    return () => {};
  }
};

const performanceTools = {
  initializePerformanceTools
};

export default performanceTools;